import React from "react";
import { Global } from "@emotion/react";

/**
 * Design-system skin for the Google Places suggestion list.
 *
 * The list is not ours to render: `google.maps.places.Autocomplete` appends a
 * `.pac-container` to `document.body` and paints it with its own stylesheet, so
 * it cannot be a Chakra `Menu` and cannot be reached from a CSS module scoped to
 * this component. A `Global` block is the only seam — the rules below re-skin
 * Google's markup with the same tokens `Menu`-like surfaces use elsewhere.
 *
 * Every value is a `--chakra-*` custom property rather than a literal: those are
 * emitted on `:root` by `ChakraProvider`, so the dropdown keeps following the
 * theme instead of drifting from it the moment a token changes.
 *
 * Selectors are prefixed with `body` on purpose. Google injects its stylesheet
 * when the SDK finishes loading, which is *after* React has mounted and emitted
 * these rules; at equal specificity the later sheet would win, so the extra
 * element bumps ours to (0,1,1) and settles it without `!important`.
 *
 * Caveat: emotion writes `Global` into the active cache. Inside a
 * `CSSIsolation` boundary that cache is a shadow root, which the light-DOM
 * `.pac-container` sits outside of — this flow is not isolated today, but a
 * screen that is would need the rules hoisted to the document cache.
 */
export const AddressAutocompleteStyles = () => (
  <Global
    styles={`
      body .pac-container {
        margin-top: 4px;
        padding: 4px 0;
        border: 1px solid var(--chakra-colors-gray-200);
        border-radius: var(--chakra-radii-lg);
        box-shadow: var(--chakra-shadows-rt-md);
        background-color: var(--chakra-colors-white);
        font-family: var(--chakra-fonts-body);
      }

      /* Google draws a hairline above every row; the padding above and the
         hover fill below already separate them. */
      body .pac-container .pac-item {
        border-top: none;
        padding: 8px 16px;
        font-size: var(--chakra-fontSizes-sm);
        line-height: var(--chakra-lineHeights-base);
        color: var(--chakra-colors-gray-600);
        cursor: pointer;
      }

      body .pac-container .pac-item:hover,
      body .pac-container .pac-item-selected,
      body .pac-container .pac-item-selected:hover {
        background-color: var(--chakra-colors-gray-100);
      }

      /* The street line, which reads as the row's title. */
      body .pac-container .pac-item-query {
        font-size: var(--chakra-fontSizes-md);
        color: var(--chakra-colors-gray-800);
        padding-right: 6px;
      }

      /* What the user has typed so far, inside the title. */
      body .pac-container .pac-matched {
        font-weight: 600;
      }

      /* Google's raster pin sprite: off-system next to our own icon set, and
         the input already carries a LocationDot of its own. */
      body .pac-container .pac-icon {
        display: none;
      }

      /* The "powered by Google" mark. Attribution is required whenever Places
         results are shown outside a Google map, so this is spaced rather than
         hidden. */
      body .pac-container.pac-logo:after {
        margin: 4px 16px 0;
        padding: 8px 0 0;
        border-top: 1px solid var(--chakra-colors-gray-200);
        background-position: right center;
      }
    `}
  />
);
